import 'swiper/swiper-bundle.css';

import { Button, Icon, Image, Modal, Typography } from '@smartgift/frontend-toolkit';
import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { VideoCard } from '@hero/components/molecules';
import { useUpdateUser } from '@hero/services/organization-service/hooks';
import { useGeneralStore } from '@hero/store/hooks';

import { TopFaqsModal } from '../TopFaqsModal';

import * as S from './style';

function WelcomeModal() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(true);
  const [isTopFaqsOpen, setIsTopFaqsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { setShowLearningCenterTooltip } = useGeneralStore();

  const { mutateAsync: onUserUpdate } = useUpdateUser();

  const STEPS = t('welcomeModalData', { returnObjects: true });

  const handleNext = () => {
    if (swiperRef?.current?.swiper) {
      swiperRef.current.swiper.slideNext();
      setActiveIndex(swiperRef.current.swiper.activeIndex);
    }
  };

  const handlePrev = () => {
    if (swiperRef?.current?.swiper) {
      swiperRef.current.swiper.slidePrev();
      setActiveIndex(swiperRef.current.swiper.activeIndex);
    }
  };

  return (
    <Modal
      size="lg"
      title={t('welcomeModalTitle')}
      primaryButtonProps={{
        children: t(activeIndex === STEPS.length - 1 ? 'gotIt' : 'next'),
        endIcon: activeIndex === STEPS.length - 1 ? '' : 'next',
        onClick: () => {
          if (activeIndex === STEPS.length - 1) {
            setIsOpen(false);
            setShowLearningCenterTooltip(true);
            onUserUpdate({ meta: { isWelcomeModalOpened: true } });
          } else {
            handleNext();
          }
        }
      }}
      secondaryButtonProps={{
        children: activeIndex === 0 ? '' : t('back'),
        variant: 'text',
        color: 'secondary',
        onClick: () => {
          if (activeIndex !== 0) {
            handlePrev();
          }
        }
      }}
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
        setShowLearningCenterTooltip(true);
        onUserUpdate({ meta: { isWelcomeModalOpened: true } });
      }}
      components={{
        LeftOfClose: () => (
          <Button
            color="secondary"
            variant="link"
            onClick={() => setIsTopFaqsOpen(true)}
            startIcon={<Icon name="faq" />}
            style={{ marginLeft: 'auto', marginRight: '24px' }}
          >
            {t('topFAQs')}
          </Button>
        )
      }}
    >
      <S.SwiperContainer>
        <Swiper
          ref={swiperRef}
          onSlideChange={swiper => {
            setActiveIndex(swiper.activeIndex);
          }}
          navigation={true}
          style={{
            '--swiper-navigation-color': 'var(--color-components-button-secondary-text-fg)',
            '--swiper-pagination-color': 'var(--color-components-button-secondary-text-fg)',
            '--swiper-navigation-size': 32
          }}
          modules={[Navigation]}
        >
          {STEPS.map((step, index) => {
            return (
              <SwiperSlide key={step.title}>
                <S.SlideContainer>
                  <S.LeftContent>
                    <Typography variant="display">
                      <Trans i18nKey={step.title} components={{ b: <b /> }} />
                    </Typography>
                    <S.LeftContentBody>
                      <S.LeftContentBodyNumber>{index + 1}</S.LeftContentBodyNumber>

                      <S.LeftContentBodyContent>
                        <Typography variant="body1">
                          <Trans i18nKey={step.description} components={{ b: <b /> }} />
                        </Typography>
                        {step.helperText && (
                          <Typography variant="body2">
                            <Trans i18nKey={step.helperText} components={{ b: <b /> }} />
                          </Typography>
                        )}
                      </S.LeftContentBodyContent>
                    </S.LeftContentBody>

                    {/* <S.Pagination>
                      {STEPS.map((step, index) => {
                        return <S.Bullet key={step.title} active={activeIndex === index} />;
                      })}
                    </S.Pagination> */}
                  </S.LeftContent>
                  <S.RightContent>
                    {step.sourceType === 'video' ? (
                      <S.VideoContainer>
                        <S.Video>
                          <VideoCard url={step.source} />
                        </S.Video>
                        <Typography variant="body1Bold">
                          {t(`welcomeModal.${index}.caption`)}
                        </Typography>
                      </S.VideoContainer>
                    ) : (
                      <Image
                        src={theme.images?.[step.source]}
                        alt={step.source}
                        style={{ width: '100%' }}
                      />
                    )}
                  </S.RightContent>
                </S.SlideContainer>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </S.SwiperContainer>

      <TopFaqsModal isOpen={isTopFaqsOpen} onClose={() => setIsTopFaqsOpen(false)} />
    </Modal>
  );
}

export { WelcomeModal };
